// =============================================
// src/transformers/vehicleTransformer.js
// LEAN DATA TRANSFORMER PATTERN
// Frontend ↔ Backend Data Mapping
// =============================================

import { BODY_TYPE_MAPPING, BODY_TYPE_DB_VALUES } from '../schemas/vehicleSchema';

import {
    COMPLETE_VEHICLE_SCHEMA,
    PERSON_BASE_SCHEMA,
    TRAFFIC_INSURANCE_SCHEMA,
    DEFAULT_VALUES
} from '../schemas/vehicleSchema.js';

// ============================
// FUEL TYPE MAPPING - TR to DB
// ============================
const FUEL_TYPE_MAPPING = {
    'Benzin': 'benzin',
    'Dizel': 'dizel',
    'LPG': 'lpg',
    'Elektrik': 'elektrik',
    'Hibrit': 'hibrit',
    'Benzin+LPG': 'benzin+lpg'
};

// ============================
// BACKEND → FRONTEND TRANSFORMERS
// ============================

/**
 * Transform backend vehicle data to frontend format
 * Handles API inconsistencies and missing fields
 */
export const transformBackendToFrontend = (backendVehicle) => {
    if (!backendVehicle) {
        console.warn('⚠️ transformBackendToFrontend: null vehicle received');
        return createEmptyVehicle();
    }

    console.log('🔄 Backend → Frontend transformation:', backendVehicle._id);

    const transformed = {
        // ✅ Basic Information - SAFE TRANSFORMATION
        title: backendVehicle.title || '',
        brand: backendVehicle.brand || '',
        model: backendVehicle.model || '',
        bodyType: BODY_TYPE_MAPPING[backendVehicle.bodyType] || backendVehicle.bodyType,
        year: backendVehicle.year || '',
        mileage: backendVehicle.mileage || '',
        color: backendVehicle.color || '',
        fuelType: backendVehicle.fuelType || DEFAULT_VALUES.fuelType,
        transmission: backendVehicle.transmission || DEFAULT_VALUES.transmission,
        description: backendVehicle.description || '',

        // ✅ YENİ: Technical Details - SAFE NESTED OBJECT TRANSFORMATION
        technicalDetails: transformTechnicalDetailsData(backendVehicle.technicalDetails),

        // ✅ Expertiz Bilgileri - YENİ EKLENEN
        expertizData: backendVehicle.expertizData || {},
        expertizNotes: backendVehicle.expertizNotes || '',

        // ✅ Financial Information - SAFE NUMBER CONVERSION
        purchasePrice: safeNumberConversion(backendVehicle.purchasePrice),
        salePrice: safeNumberConversion(backendVehicle.salePrice),
        purchaseDate: safeDateConversion(backendVehicle.purchaseDate),
        saleDate: safeDateConversion(backendVehicle.saleDate),

        // ✅ Legal Information - UPPERCASE SAFETY
        plateNumber: (backendVehicle.plateNumber || '').toUpperCase(),
        chassisNumber: (backendVehicle.chassisNumber || '').toUpperCase(),
        engineNumber: (backendVehicle.engineNumber || '').toUpperCase(),
        invoiceNumber: (backendVehicle.invoiceNumber || '').toUpperCase(),


        // ✅ Tramer Information - SAFE BOOLEAN + NUMBER
        hasTramer: Boolean(backendVehicle.hasTramer),
        tramerAmount: safeNumberConversion(backendVehicle.tramerAmount, 0),
        tramerDescription: backendVehicle.tramerDescription || '',


        // ✅ Status Management
        status: backendVehicle.status || DEFAULT_VALUES.status,

        // ✅ System Fields
        customId: backendVehicle.customId || null,
        viewCount: safeNumberConversion(backendVehicle.viewCount, 0),
        addedBy: backendVehicle.addedBy || null,
        createdAt: backendVehicle.createdAt || null,
        updatedAt: backendVehicle.updatedAt || null,

        // ✅ Arrays - SAFE ARRAY CONVERSION
        images: Array.isArray(backendVehicle.images) ? backendVehicle.images : [],
        features: Array.isArray(backendVehicle.features) ? backendVehicle.features : [],
        expenses: Array.isArray(backendVehicle.expenses) ? backendVehicle.expenses : [],
        documents: Array.isArray(backendVehicle.documents) ? backendVehicle.documents : [],

        // ✅ Person Information - DEEP SAFE TRANSFORMATION
        purchasedFrom: transformPersonData(backendVehicle.purchasedFrom, {
            name: DEFAULT_VALUES.purchasedFromName
        }),
        soldTo: transformPersonData(backendVehicle.soldTo),

        // ✅ Traffic Insurance - DEEP SAFE TRANSFORMATION
        trafficInsurance: transformTrafficInsuranceData(backendVehicle.trafficInsurance)


    };

    // ✅ DEBUG LOG - DOĞRU YER
    console.log('🔍 EXPERTIZ DEBUG - Backend to Frontend:', {
        backendExpertizData: backendVehicle.expertizData,
        backendExpertizNotes: backendVehicle.expertizNotes,
        frontendExpertizData: transformed.expertizData,
        frontendExpertizNotes: transformed.expertizNotes
    });

    console.log('✅ Frontend transformation completed');
    return transformed;
};

/**
 * Transform person data safely
 */
const transformPersonData = (personData, defaults = {}) => {
    if (!personData || typeof personData !== 'object') {
        return { ...PERSON_BASE_SCHEMA, ...defaults };
    }

    return {
        name: personData.name || defaults.name || '',
        phone: personData.phone || '',
        email: personData.email || '',
        address: personData.address || '',
        idNumber: personData.idNumber || ''
    };
};

// transformPersonData fonksiyonundan sonra ekle:

/**
 * Transform technical details data safely for frontend
 */
const transformTechnicalDetailsData = (technicalDetailsData) => {
    if (!technicalDetailsData || typeof technicalDetailsData !== 'object') {
        return {
            engineDisplacement: '',
            horsepower: '',
            warrantyStatus: '',
            inspectionDate: '',
            sahibindenUrl: '',
            labelDescription: ''
        };
    }

    return {
        engineDisplacement: technicalDetailsData.engineDisplacement || '',
        horsepower: technicalDetailsData.horsepower || '',
        warrantyStatus: technicalDetailsData.warrantyStatus || '',
        inspectionDate: safeDateConversion(technicalDetailsData.inspectionDate),
        sahibindenUrl: technicalDetailsData.sahibindenUrl || '',
        labelDescription: technicalDetailsData.labelDescription || ''
    };
};

/**
 * Transform traffic insurance data safely
 */
const transformTrafficInsuranceData = (insuranceData) => {
    if (!insuranceData || typeof insuranceData !== 'object') {
        return { ...TRAFFIC_INSURANCE_SCHEMA };
    }

    return {
        // Basic Insurance Info
        hasInsurance: Boolean(insuranceData.hasInsurance),
        startDate: safeDateConversion(insuranceData.startDate),
        endDate: safeDateConversion(insuranceData.endDate),
        insuranceCompany: insuranceData.insuranceCompany || '',
        paymentCardName: insuranceData.paymentCardName || '',
        insuranceAmount: safeNumberConversion(insuranceData.insuranceAmount, 0),
        policyDocument: insuranceData.policyDocument || '',
        policyNumber: insuranceData.policyNumber || '',
        notes: insuranceData.notes || '',

        // Cancellation Info
        isCancelled: Boolean(insuranceData.isCancelled),
        cancellationDate: safeDateConversion(insuranceData.cancellationDate),
        cancellationPolicyDocument: insuranceData.cancellationPolicyDocument || '',
        refundAmount: safeNumberConversion(insuranceData.refundAmount, 0),
        cancellationReason: insuranceData.cancellationReason || '',
        cancellationNotes: insuranceData.cancellationNotes || ''
    };
};

// ============================
// FRONTEND → BACKEND TRANSFORMERS
// ============================

/**
 * Transform frontend vehicle data to backend format
 * Removes null/undefined, ensures proper types
 */
export const transformFrontendToBackend = (frontendVehicle) => {
    if (!frontendVehicle) {
        throw new Error('transformFrontendToBackend: vehicle data is required');
    }

    console.log('🔄 Frontend → Backend transformation');

    const transformed = {
        // ✅ Basic Information - CLEAN & VALIDATE
        title: cleanString(frontendVehicle.title),
        brand: cleanString(frontendVehicle.brand),
        model: cleanString(frontendVehicle.model),
        bodyType: BODY_TYPE_DB_VALUES[frontendVehicle.bodyType] || frontendVehicle.bodyType?.toLowerCase(),
        year: safeNumberConversion(frontendVehicle.year),
        mileage: safeNumberConversion(frontendVehicle.mileage),
        color: cleanString(frontendVehicle.color),
        fuelType: FUEL_TYPE_MAPPING[frontendVehicle.fuelType] || frontendVehicle.fuelType?.toLowerCase() || DEFAULT_VALUES.fuelType,
        transmission: frontendVehicle.transmission || DEFAULT_VALUES.transmission,
        description: cleanString(frontendVehicle.description),

        // ✅ Financial Information - PROPER NUMBERS
        purchasePrice: safeNumberConversion(frontendVehicle.purchasePrice),
        salePrice: frontendVehicle.salePrice ? safeNumberConversion(frontendVehicle.salePrice) : null,
        purchaseDate: safeDateForBackend(frontendVehicle.purchaseDate),
        saleDate: frontendVehicle.saleDate ? safeDateForBackend(frontendVehicle.saleDate) : null,

        // ✅ Legal Information - UPPERCASE + VALIDATION
        plateNumber: cleanString(frontendVehicle.plateNumber, '').toUpperCase(),
        chassisNumber: cleanString(frontendVehicle.chassisNumber, '').toUpperCase(),
        engineNumber: cleanString(frontendVehicle.engineNumber, '').toUpperCase(),
        invoiceNumber: cleanString(frontendVehicle.invoiceNumber, '').toUpperCase(),


        // ✅ Tramer Information
        hasTramer: Boolean(frontendVehicle.hasTramer),
        tramerAmount: frontendVehicle.hasTramer ? safeNumberConversion(frontendVehicle.tramerAmount, 0) : 0,
        tramerDescription: frontendVehicle.hasTramer ? cleanString(frontendVehicle.tramerDescription) : '',

        // ✅ Status Management
        status: frontendVehicle.status || DEFAULT_VALUES.status,

        // ✅ Arrays - CLEAN ARRAYS
        images: Array.isArray(frontendVehicle.images) ? frontendVehicle.images : [],
        features: Array.isArray(frontendVehicle.features) ?
            frontendVehicle.features.filter(f => f && f.trim()) : [],
        expenses: Array.isArray(frontendVehicle.expenses) ? frontendVehicle.expenses : [],

        // ✅ Person Information - CLEAN OBJECTS
        purchasedFrom: transformPersonForBackend(frontendVehicle.purchasedFrom),
        soldTo: frontendVehicle.soldTo && frontendVehicle.soldTo.name ?
            transformPersonForBackend(frontendVehicle.soldTo) : null,

        // ✅ Traffic Insurance - CLEAN OBJECT
        trafficInsurance: transformTrafficInsuranceForBackend(frontendVehicle.trafficInsurance),

       
        // ✅ YENİ: Technical Details - CLEAN NESTED OBJECT
        technicalDetails: transformTechnicalDetailsForBackend(frontendVehicle.technicalDetails),

        // ✅ Expertiz Bilgileri - YENİ EKLENEN
        expertizData: frontendVehicle.expertizData || {},
        expertizNotes: cleanString(frontendVehicle.expertizNotes)

    };

    // ✅ DEBUG LOG - DOĞRU YER
    console.log('🔍 EXPERTIZ DEBUG - Frontend to Backend:', {
        frontendExpertizData: frontendVehicle.expertizData,
        frontendExpertizNotes: frontendVehicle.expertizNotes,
        backendExpertizData: transformed.expertizData,
        backendExpertizNotes: transformed.expertizNotes
    });

    // ✅ REMOVE NULL/UNDEFINED VALUES (LEAN PRINCIPLE)
    const cleanedTransformed = removeEmptyValues(transformed);

    console.log('✅ Backend transformation completed');
    return cleanedTransformed;
};

/**
 * Transform technical details for backend
 */
const transformTechnicalDetailsForBackend = (technicalDetailsData) => {
    if (!technicalDetailsData || typeof technicalDetailsData !== 'object') {
        return {};
    }

    const cleaned = {};

    // Engine Displacement
    if (technicalDetailsData.engineDisplacement) {
        const displacement = safeNumberConversion(technicalDetailsData.engineDisplacement);
        if (displacement !== null && displacement > 0) {
            cleaned.engineDisplacement = displacement;
        }
    }

    // Horsepower
    if (technicalDetailsData.horsepower) {
        const hp = safeNumberConversion(technicalDetailsData.horsepower);
        if (hp !== null && hp > 0) {
            cleaned.horsepower = hp;
        }
    }

    // Warranty Status
    if (technicalDetailsData.warrantyStatus) {
        cleaned.warrantyStatus = cleanString(technicalDetailsData.warrantyStatus);
    }

    // Inspection Date
    if (technicalDetailsData.inspectionDate) {
        cleaned.inspectionDate = safeDateForBackend(technicalDetailsData.inspectionDate);
    }

    // Sahibinden URL
    if (technicalDetailsData.sahibindenUrl) {
        const url = cleanString(technicalDetailsData.sahibindenUrl);
        // Basic URL validation
        if (url.startsWith('http')) {
            cleaned.sahibindenUrl = url;
        }
    }

    // Label Description
    if (technicalDetailsData.labelDescription) {
        cleaned.labelDescription = cleanString(technicalDetailsData.labelDescription);
    }

    return cleaned;
};

/**
 * Transform person data for backend
 */
const transformPersonForBackend = (personData) => {
    if (!personData || !personData.name) {
        return null;
    }

    return {
        name: cleanString(personData.name),
        phone: cleanString(personData.phone) || null,
        email: cleanString(personData.email) || null,
        address: cleanString(personData.address) || null,
        idNumber: cleanString(personData.idNumber)
    };
};

/**
 * Transform traffic insurance for backend
 */
const transformTrafficInsuranceForBackend = (insuranceData) => {
    console.log('🔍 TRANSFORM TrafficInsurance INPUT:', insuranceData);

    if (!insuranceData) {
        return {
            startDate: null,
            endDate: null,
            paymentCardName: '',
            insuranceAmount: 0,
            refundAmount: 0
        };
    }

    console.log('🔍 Raw startDate:', insuranceData.startDate);
    console.log('🔍 Raw endDate:', insuranceData.endDate);

    const result = {
        startDate: insuranceData.startDate ? safeDateForBackend(insuranceData.startDate) : null,
        endDate: insuranceData.endDate ? safeDateForBackend(insuranceData.endDate) : null,
        paymentCardName: cleanString(insuranceData.paymentCardName),
        insuranceAmount: safeNumberConversion(insuranceData.insuranceAmount, 0),
        refundAmount: safeNumberConversion(insuranceData.refundAmount, 0)
    };

    console.log('🔍 After transformation:');
    console.log('- startDate result:', result.startDate);
    console.log('- endDate result:', result.endDate);

    console.log('🔍 TRANSFORM TrafficInsurance OUTPUT:', result);
    return result;
};

// ============================
// UTILITY FUNCTIONS (LEAN DRY)
// ============================

/**
 * Safe number conversion
 */
const safeNumberConversion = (value, defaultValue = null) => {
    if (value === null || value === undefined || value === '') {
        return defaultValue;
    }

    const number = Number(value);
    return isNaN(number) ? defaultValue : number;
};

/**
 * Safe date conversion for frontend (ISO string to date input format)
 */
const safeDateConversion = (dateValue) => {
    if (!dateValue) return '';

    try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    } catch (error) {
        console.warn('Date conversion error:', error);
        return '';
    }
};

/**
 * Safe date conversion for backend (date input format to Date object)
 */
const safeDateForBackend = (dateValue) => {
    if (!dateValue) return null;

    try {
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    } catch (error) {
        console.warn('Backend date conversion error:', error);
        return null;
    }
};

/**
 * Clean string values
 */
const cleanString = (value, defaultValue = '') => {
    if (value === null || value === undefined) {
        return defaultValue;
    }
    return String(value).trim();
};

/**
 * Remove empty values from object (LEAN principle)
 */
const removeEmptyValues = (obj) => {
    const cleaned = {};

    Object.entries(obj).forEach(([key, value]) => {
        // ✅ EXPERTIZ DEBUG
        if (key === 'expertizData' && value) {
            console.log('🔍 EXPERTIZ DEBUG - removeEmptyValues processing expertizData:', value);
        }
        if (key === 'expertizNotes' && value) {
            console.log('🔍 EXPERTIZ DEBUG - removeEmptyValues processing expertizNotes:', value);
        }

        // ✅ SPECIAL CASE: Price fields should allow null to indicate deletion
        if (key === 'salePrice' || key === 'purchasePrice') {
            cleaned[key] = value; // Include even if null
            return;
        }

        // ✅ SPECIAL CASE: Date fields should allow null to indicate deletion
        if (key === 'saleDate' || key === 'purchaseDate') {
            cleaned[key] = value; // Include even if null
            return;
        }

        // ✅ SPECIAL CASE: Description field should allow empty string to clear content
        if (key === 'description') {
            cleaned[key] = value || ''; // Include even if empty string
            return;
        }

        if (value !== null && value !== undefined && value !== '') {
            if (typeof value === 'object' && !Array.isArray(value)) {
                // ✅ DATE OBJECT EXCEPTION
                if (value instanceof Date) {
                    cleaned[key] = value; // Keep Date objects
                } else {
                    const cleanedNestedObj = removeEmptyValues(value);
                    if (Object.keys(cleanedNestedObj).length > 0) {
                        cleaned[key] = cleanedNestedObj;
                    }
                }
            } else if (Array.isArray(value)) {
                if (value.length > 0) {
                    cleaned[key] = value;
                }
            } else {
                cleaned[key] = value;
            }
        }
    });

    return cleaned;
};

/**
 * Create empty vehicle (LEAN factory)
 */
const createEmptyVehicle = () => {
    return {
        ...COMPLETE_VEHICLE_SCHEMA,
        purchasedFrom: { ...PERSON_BASE_SCHEMA, name: DEFAULT_VALUES.purchasedFromName },
        soldTo: { ...PERSON_BASE_SCHEMA },
        trafficInsurance: { ...TRAFFIC_INSURANCE_SCHEMA, hasInsurance: true },

        // ✅ YENİ: Technical Details default
        technicalDetails: {
            engineDisplacement: '',
            horsepower: '',
            warrantyStatus: '',
            inspectionDate: '',
            sahibindenUrl: '',
            labelDescription: ''
        },

        // ✅ YENİ: Expertiz defaults
        expertizData: {},
        expertizNotes: ''
    };
};

// ============================
// SPECIALIZED TRANSFORMERS
// ============================

/**
 * Transform vehicle for API create request
 */
export const transformForApiCreate = (frontendVehicle) => {
    const backendData = transformFrontendToBackend(frontendVehicle);

    // ✅ FIX: Remove fields that backend doesn't expect for creation
    delete backendData.customId;
    delete backendData.createdAt;
    delete backendData.updatedAt;
    delete backendData.viewCount;

    return backendData;
};

/**
 * Transform vehicle for API update request
 */
export const transformForApiUpdate = (frontendVehicle) => {
    const backendData = transformFrontendToBackend(frontendVehicle);

    // ✅ Keep system fields for updates
    return backendData;
};

/**
 * Transform API response to frontend format
 */
export const transformApiResponse = (apiResponse) => {
    if (apiResponse.success && apiResponse.vehicle) {
        return {
            success: true,
            vehicle: transformBackendToFrontend(apiResponse.vehicle),
            message: apiResponse.message
        };
    }

    if (apiResponse.success && apiResponse.vehicles) {
        return {
            success: true,
            vehicles: apiResponse.vehicles.map(transformBackendToFrontend),
            count: apiResponse.count,
            total: apiResponse.total,
            pages: apiResponse.pages,
            currentPage: apiResponse.currentPage
        };
    }

    return apiResponse;
};

/**
 * ✅ FIX: Clear specific vehicle cache after update
 */
export const invalidateVehicleCache = (vehicleId) => {
    if (typeof window !== 'undefined' && window.vehicleCacheManager) {
        // Clear all vehicle-related cache
        window.vehicleCacheManager.clearPattern('vehicles');
        window.vehicleCacheManager.clearPattern(vehicleId);
        console.log(`🧹 Cache invalidated for vehicle: ${vehicleId}`);
    }
};

// ============================
// EXPORT
// ============================

export default {
    transformBackendToFrontend,
    transformFrontendToBackend,
    transformForApiCreate,
    transformForApiUpdate,
    transformApiResponse,

    // Utilities
    safeNumberConversion,
    safeDateConversion,
    cleanString
};